import { useLayoutEffect, useRef, useState } from 'react'

export interface Block {
  key: string
  node: React.ReactNode
  /** Blocks with the same groupId prefer to stay on the same page as the next
   *  block (e.g. a section heading kept with its first row). */
  keepWithNext?: boolean
}

/**
 * Measures every block once (hidden) and packs them into pages that never
 * exceed `pageHeightPx`. Deterministic once measured. A heading flagged
 * keepWithNext is pushed to the next page if its following block wouldn't fit
 * beside it, so headings never strand at a page bottom.
 */
export function useMeasuredPages(blocks: Block[], pageHeightPx: number) {
  const refs = useRef<Record<string, HTMLElement | null>>({})
  const [pages, setPages] = useState<Block[][] | null>(null)

  const setRef = (key: string) => (el: HTMLElement | null) => {
    refs.current[key] = el
  }

  useLayoutEffect(() => {
    const measure = () => {
      const heights: Record<string, number> = {}
      for (const b of blocks) {
        const el = refs.current[b.key]
        if (!el) return false
        const h = el.getBoundingClientRect().height
        if (h === 0) return false
        heights[b.key] = h
      }

      const GAP = 20 // vertical rhythm between blocks
      const result: Block[][] = []
      let current: Block[] = []
      let used = 0

      const flush = () => {
        if (current.length) result.push(current)
        current = []
        used = 0
      }

      for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i]
        const h = heights[b.key]
        const add = (current.length ? GAP : 0) + h

        if (current.length > 0 && used + add > pageHeightPx) {
          flush()
        }

        // Keep-with-next: if this is a heading and the next block won't also
        // fit on the current (fresh) page, defer both — but only if we're not
        // already at the very top (avoid infinite defer for oversized content).
        if (b.keepWithNext && i + 1 < blocks.length) {
          const nextH = heights[blocks[i + 1].key]
          const projected = (current.length ? used + GAP : 0) + h + GAP + nextH
          if (current.length > 0 && projected > pageHeightPx) {
            flush()
          }
        }

        current.push(b)
        used += current.length === 1 ? h : add
      }
      flush()

      setPages(result)
      return true
    }

    let done = false
    const tryMeasure = () => {
      if (done) return
      if (measure()) done = true
    }

    tryMeasure()
    const timers = [30, 100, 250, 500, 900, 1500, 2500].map((t) =>
      window.setTimeout(tryMeasure, t),
    )
    // Re-measure once web fonts have loaded (they change block heights).
    if (typeof document !== 'undefined' && (document as any).fonts?.ready) {
      ;(document as any).fonts.ready.then(tryMeasure)
    }
    return () => timers.forEach(window.clearTimeout)
  }, [blocks, pageHeightPx])

  return { setRef, pages }
}
